import { Card, Col, Container, Form, Row, Spinner } from "react-bootstrap";
import { useEffect, useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { BiSolidError } from "react-icons/bi";
import { Input } from "../../components/Input/input";
import {
  validateRecoveryEmailChange,
  validateRepeatRecoveryEmailChange,
} from "../../utils/validators";
import Button, { ButtonColors, ButtonTypes } from "../../components/Button";
import { RecoveryAccountHeader } from "../../components/RecoveryAccountHeader/RecoveryAccountHeader";
import { UserService } from "../../services/UsersService";
import {
  RECOVERY_EMAIL_CHANGE_JWT_STORAGE_KEY,
  hydrateRecoveryJwtFromUrlOrStorage,
  clearRecoveryJwtStorage,
  stripRecoveryJwtFromUrlHash,
} from "../../utils/recoveryFlowStorage";

type FormValues = {
  recoveryEmail: string;
  repeatRecoveryEmail: string;
};

function getErrorMessage(err: unknown): string {
  const ax = err as {
    response?: { data?: { message?: string | string[] } };
  };
  const msg = ax.response?.data?.message;
  if (Array.isArray(msg)) {
    const parts = msg.filter((m) => m != null && String(m).trim() !== "");
    if (parts.length > 0) return parts.join(" ");
  }
  if (typeof msg === "string" && msg.trim()) return msg.trim();
  return "Não foi possível atualizar o e-mail de recuperação. Verifique os dados e tente novamente.";
}

export function ChangeRecoveryEmail() {
  const [recoveryToken] = useState(() =>
    hydrateRecoveryJwtFromUrlOrStorage(RECOVERY_EMAIL_CHANGE_JWT_STORAGE_KEY),
  );

  useLayoutEffect(() => {
    stripRecoveryJwtFromUrlHash();
  }, []);

  const [phase, setPhase] = useState<
    "loading" | "invalid" | "ready" | "success"
  >("loading");
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormValues>({
    defaultValues: { recoveryEmail: "", repeatRecoveryEmail: "" },
  });
  const { handleSubmit } = form;

  useEffect(() => {
    if (!recoveryToken) {
      setPhase("invalid");
      return;
    }

    let cancelled = false;
    UserService.validateRecoveryEmailChangeToken(recoveryToken)
      .then(() => {
        if (!cancelled) setPhase("ready");
      })
      .catch(() => {
        if (!cancelled) {
          clearRecoveryJwtStorage(RECOVERY_EMAIL_CHANGE_JWT_STORAGE_KEY);
          setPhase("invalid");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [recoveryToken]);

  function onSubmit(data: FormValues) {
    setSubmitting(true);
    UserService.completeRecoveryEmailChange(
      recoveryToken,
      data.recoveryEmail.trim(),
    )
      .then(() => {
        clearRecoveryJwtStorage(RECOVERY_EMAIL_CHANGE_JWT_STORAGE_KEY);
        form.reset({ recoveryEmail: "", repeatRecoveryEmail: "" });
        setPhase("success");
      })
      .catch((err) => {
        const msg = getErrorMessage(err);
        toast.error(msg);
      })
      .finally(() => setSubmitting(false));
  }

  if (phase === "loading") {
    return (
      <Container fluid className="px-3">
        <RecoveryAccountHeader title="E-mail de recuperação" />
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "60vh" }}
        >
          <Spinner animation="border" style={{ color: "#005ca8" }} />
          <p className="mt-3 mb-0 text-muted">Validando link…</p>
        </div>
      </Container>
    );
  }

  if (phase === "invalid") {
    return (
      <Container fluid className="px-3">
        <RecoveryAccountHeader title="E-mail de recuperação" />
        <div
          className="d-flex flex-column align-items-center justify-content-center"
          style={{ minHeight: "60vh" }}
        >
          <Card style={{ padding: 24, maxWidth: 480 }}>
            <Card.Title style={{ textAlign: "center" }}>
              <span className="d-inline-flex align-items-center gap-2 justify-content-center">
                <BiSolidError style={{ fontSize: 22, color: "#EF4F4F" }} />
                Link inválido, expirado ou já utilizado.
              </span>
            </Card.Title>
            <Card.Body className="text-center text-muted small">
              Solicite novamente pela área Informações do perfil ou entre em
              contato com o suporte.
            </Card.Body>
          </Card>
        </div>
      </Container>
    );
  }

  if (phase === "success") {
    return (
      <Container fluid className="px-3">
        <RecoveryAccountHeader title="E-mail de recuperação" />
        <Row className="justify-content-center">
          <Col xs={12} md={8} lg={5}>
            <Card className="shadow-sm">
              <Card.Body className="p-4 text-center">
                <Card.Title className="mb-3">Alteração concluída</Card.Title>
                <p className="text-muted mb-0">
                  O e-mail de recuperação foi atualizado com sucesso. Você pode
                  fechar esta página.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3 pb-5">
      <RecoveryAccountHeader title="E-mail de recuperação" />
      <Row className="justify-content-center">
        <Col xs={12} md={8} lg={5}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <Card.Title className="mb-3">Novo e-mail de recuperação</Card.Title>
              <p className="text-muted small mb-4">
                Informe o novo e-mail de recuperação. Este link só pode ser
                usado uma vez.
              </p>
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="gy-3">
                  <Col xs={12}>
                    <Input
                      inputName="recoveryEmail"
                      label="E-mail de recuperação"
                      form={form}
                      validators={{ validate: validateRecoveryEmailChange }}
                    />
                  </Col>
                  <Col xs={12}>
                    <Input
                      inputName="repeatRecoveryEmail"
                      label="Confirmar e-mail de recuperação"
                      form={form}
                      validators={{
                        validate: validateRepeatRecoveryEmailChange(form),
                      }}
                    />
                  </Col>
                  <Col xs={12} className="pt-2 d-flex justify-content-end">
                    <Button
                      text={
                        submitting
                          ? "Salvando…"
                          : "Confirmar novo e-mail de recuperação"
                      }
                      color={ButtonColors.primary}
                      type={ButtonTypes.submit}
                      disabled={submitting}
                    />
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
